// Global
import { Text, TextField } from '@sitecore-content-sdk/nextjs';

export type TextProps = React.ComponentProps<typeof Text>;
import React from 'react';

//Local
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { useFieldWithFallbacks } from 'lib/hooks/sitecore/useFieldWithFallbacks';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';
import { ReplacementToken, tokenReplace } from 'lib/utils/string-utils';

export interface PlainTextWrapperProps extends TextProps, React.HTMLAttributes<HTMLHeadingElement> {
  fallbacks?: (TextField | undefined)[];
  tokens?: ReplacementToken[];
}

/**
 * This hook is used to render a plain text field.
 * This is needed because a component cannot return null.
 * This needs to be a hook because we need to use the useIsEditing hook to determine if we are in edit mode.
 * @param props - The props for the plain text wrapper.
 * @returns A JSX.Element if the field has a value, or if we are editing, otherwise null.
 */
export function usePlainTextWrapper(props: PlainTextWrapperProps): JSX.Element | null {
  const { field, fallbacks, tokens, editable = true } = props;
  const isEditing = useIsEditing() && editable;

  const { renderField } = useFieldWithFallbacks(field, fallbacks);

  const updatedField = useUpdatedPlainTextField({
    field: renderField,
    tokens,
    editable,
  });
  const hasValue = !!updatedField?.value;

  // We should only render if it has a value, or if we are editing
  const shouldRender = hasValue || isEditing;

  if (!shouldRender) return null;

  return <PlainTextWrapper {...props} />;
}

const PlainTextWrapper = ({
  field,
  fallbacks,
  tokens,
  editable = true,
  ...props
}: PlainTextWrapperProps): JSX.Element => {
  const isEditing = useIsEditing() && editable;

  const { renderField, fallbackFieldForEditing } = useFieldWithFallbacks(field, fallbacks);

  const updatedField = useUpdatedPlainTextField({
    field: renderField,
    tokens,
    editable,
  });
  const hasValue = !!updatedField?.value;

  // We should only render if it has a value, or if we are editing
  const shouldRender = hasValue || isEditing;

  if (!shouldRender) return <></>;

  if (isEditing && fallbackFieldForEditing && fallbackFieldForEditing !== renderField) {
    return (
      <span>
        <Text
          {...props}
          data-component="helpers/fieldwrappers/plaintextwrapper"
          editable={editable}
          field={updatedField}
        />

        <EditingHelpText>
          Fallback:{' '}
          <Text
            {...props}
            data-component="helpers/fieldwrappers/plaintextwrapper"
            editable={false}
            field={fallbackFieldForEditing}
          />
        </EditingHelpText>
      </span>
    );
  }
  return (
    <Text
      {...props}
      data-component="helpers/fieldwrappers/plaintextwrapper"
      editable={editable}
      field={updatedField}
    />
  );
};

function useUpdatedPlainTextField({ field, tokens, editable = true }: PlainTextWrapperProps) {
  const isEditing = useIsEditing() && editable;

  const plainTextContent = tokens ? tokenReplace(field?.value?.toString(), tokens) : field?.value;

  const updatedField: TextField = {
    value: plainTextContent || '',
  };

  return isEditing ? field : updatedField;
}

export default PlainTextWrapper;
